import { useState, useEffect } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { 
  FileStack, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Copy, 
  Eye,
  FileText,
  FileSignature,
  Mail,
  Loader2,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const AdminTemplatesPage = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'contract',
    description: '',
    content: '',
    isActive: true
  });

  // Sample templates data
  const sampleTemplates = [
    {
      id: 'tmpl_001',
      name: 'Contrato de Inversión Individual',
      type: 'contract',
      description: 'Contrato estándar para inversionistas individuales',
      variables: ['{{investor_name}}', '{{investment_amount}}', '{{opportunity_name}}', '{{date}}'],
      isActive: true,
      lastModified: '2026-03-10',
      usageCount: 45
    },
    {
      id: 'tmpl_002',
      name: 'Contrato de Inversión Entidad',
      type: 'contract',
      description: 'Contrato para personas morales y entidades corporativas',
      variables: ['{{entity_name}}', '{{representative}}', '{{investment_amount}}', '{{opportunity_name}}'],
      isActive: true,
      lastModified: '2026-03-08',
      usageCount: 23
    },
    {
      id: 'tmpl_003',
      name: 'Carta de Compromiso',
      type: 'commitment',
      description: 'Carta de compromiso de inversión',
      variables: ['{{investor_name}}', '{{amount}}', '{{deadline}}'],
      isActive: true,
      lastModified: '2026-02-28',
      usageCount: 67
    },
    {
      id: 'tmpl_004',
      name: 'Email de Bienvenida',
      type: 'email',
      description: 'Email de bienvenida para nuevos inversionistas',
      variables: ['{{name}}', '{{email}}'],
      isActive: true,
      lastModified: '2026-02-15',
      usageCount: 128
    },
    {
      id: 'tmpl_005',
      name: 'Notificación de Oportunidad',
      type: 'email',
      description: 'Email para notificar nuevas oportunidades de inversión',
      variables: ['{{investor_name}}', '{{opportunity_title}}', '{{irr}}', '{{minimum}}'],
      isActive: true,
      lastModified: '2026-03-01',
      usageCount: 89
    },
    {
      id: 'tmpl_006',
      name: 'Acuse de Recibo KYC',
      type: 'document',
      description: 'Documento de acuse de recibo de documentación KYC',
      variables: ['{{investor_name}}', '{{documents_received}}', '{{date}}'],
      isActive: false,
      lastModified: '2026-01-20',
      usageCount: 34
    }
  ];

  useEffect(() => {
    setTimeout(() => {
      setTemplates(sampleTemplates);
      setLoading(false);
    }, 500);
  }, []);

  const templateTypes = [
    { value: 'contract', label: 'Contrato', icon: FileSignature },
    { value: 'commitment', label: 'Compromiso', icon: FileText },
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'document', label: 'Documento', icon: FileStack }
  ];

  const getTypeIcon = (type) => {
    const typeConfig = templateTypes.find(t => t.value === type);
    if (!typeConfig) return FileStack;
    return typeConfig.icon;
  };

  const getTypeBadge = (type) => {
    const colors = {
      contract: 'bg-purple-500/10 text-purple-500',
      commitment: 'bg-blue-500/10 text-blue-500',
      email: 'bg-green-500/10 text-green-500',
      document: 'bg-orange-500/10 text-orange-500'
    };
    const labels = {
      contract: 'Contrato',
      commitment: 'Compromiso',
      email: 'Email',
      document: 'Documento'
    };
    return (
      <Badge className={`${colors[type] || 'bg-muted'} border-0`}>
        {labels[type] || type}
      </Badge>
    );
  };

  const filteredTemplates = templates.filter(tmpl => 
    tmpl.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tmpl.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      type: 'contract',
      description: '',
      content: '',
      isActive: true
    });
    setShowDialog(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      type: template.type,
      description: template.description,
      content: template.content || '',
      isActive: template.isActive
    });
    setShowDialog(true);
  };

  const handleSave = () => {
    if (editingTemplate) {
      // Update existing
      setTemplates(templates.map(t => 
        t.id === editingTemplate.id 
          ? { ...t, ...formData, lastModified: new Date().toISOString().split('T')[0] }
          : t
      ));
    } else {
      // Create new
      const newTemplate = {
        id: `tmpl_${Date.now()}`,
        ...formData,
        variables: [],
        lastModified: new Date().toISOString().split('T')[0],
        usageCount: 0
      };
      setTemplates([newTemplate, ...templates]);
    }
    setShowDialog(false);
  };

  const handleDelete = (id) => {
    if (confirm('¿Estás seguro de eliminar esta plantilla?')) {
      setTemplates(templates.filter(t => t.id !== id));
    }
  };

  const handleDuplicate = (template) => {
    const duplicate = {
      ...template,
      id: `tmpl_${Date.now()}`,
      name: `${template.name} (Copia)`,
      usageCount: 0,
      lastModified: new Date().toISOString().split('T')[0]
    };
    setTemplates([duplicate, ...templates]);
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-light tracking-tight">Gestión de Plantillas</h1>
          <p className="text-muted-foreground mt-1">
            Administra las plantillas de documentos y comunicaciones
          </p>
        </div>
        <Button onClick={handleCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          Nueva Plantilla
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {templateTypes.map(type => {
          const Icon = type.icon;
          const count = templates.filter(t => t.type === type.value).length;
          return (
            <div key={type.value} className="card-premium p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-light">{count}</p>
                  <p className="text-xs text-muted-foreground">{type.label}s</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar plantillas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.length === 0 ? (
            <div className="col-span-full p-8 text-center text-muted-foreground card-premium">
              <FileStack className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No se encontraron plantillas</p>
            </div>
          ) : (
            filteredTemplates.map((template) => {
              const Icon = getTypeIcon(template.type);
              return (
                <div key={template.id} className="card-premium p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                        <Icon className="h-5 w-5" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="font-medium text-sm">{template.name}</h3>
                        {getTypeBadge(template.type)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {template.isActive ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                    {template.description}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                    <span>Modificado: {template.lastModified}</span>
                    <span>{template.usageCount} usos</span>
                  </div>
                  
                  {template.variables && template.variables.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-muted-foreground mb-1">Variables:</p>
                      <div className="flex flex-wrap gap-1">
                        {template.variables.slice(0, 3).map((v, i) => (
                          <Badge key={i} variant="outline" className="text-xs font-mono">
                            {v}
                          </Badge>
                        ))}
                        {template.variables.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{template.variables.length - 3}
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 pt-3 border-t border-border">
                    <Button variant="ghost" size="sm" className="flex-1" onClick={() => handleEdit(template)}>
                      <Edit className="h-3 w-3 mr-1" /> Editar
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDuplicate(template)}>
                      <Copy className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(template.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Editar Plantilla' : 'Nueva Plantilla'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nombre de la plantilla"
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {templateTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción breve de la plantilla"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Contenido</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Contenido de la plantilla. Usa {{variable}} para campos dinámicos."
                rows={10}
                className="font-mono text-sm"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleSave}>
              {editingTemplate ? 'Guardar Cambios' : 'Crear Plantilla'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTemplatesPage;
